import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Collapse,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch } from 'react-redux';
import {
  getEquipmentTypes,
  getManufacturers,
  getEquipmentModels,
  getBusinessCategories,
  getLeases,
  getMerchantServiceProviders,
} from '../../store/slices/lookupSlice';
import { extractList } from '../../utils/response';
import LeadAttachmentsUpload from './LeadAttachmentsUpload';
import {
  isValidEmail,
  createEmptyOfferedService,
  normalizeOfferedServices,
  getOfferedServiceContent,
  OFFERED_SERVICE_MAX_LENGTH,
} from './leadFormHelpers';
/** Match CreateLead.jsx view-mode field styling */
const viewModeTitleStyle = {
  color: 'text.secondary',
  fontWeight: 600,
  mb: 1,
};

const viewModeFieldStyle = {
  p: 1.5,
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 1,
  minHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'grey.50',
  color: 'text.primary',
};

const viewModeTextAreaStyle = {
  ...viewModeFieldStyle,
  minHeight: '80px',
  alignItems: 'flex-start',
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
  },
};

export default function LeadAdditionalDetailsSection({
  formData,
  setFormData,
  disabled = false,
  isViewMode = false,
  leadId = null,
  assignees =[],
  attachmentsHydrateToken = 0,
  defaultOpen = false,
  hideAttachments = true,
  showOfferServices = false,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(defaultOpen || showOfferServices);
  const lookupsLoaded = useRef(false);

  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [equipmentModelOptions, setEquipmentModelOptions] = useState([]);
  const [businessCategoryOptions, setBusinessCategoryOptions] = useState([]);
  const [leaseOptions, setLeaseOptions] = useState([]);
  const [merchantServiceProviderOptions, setMerchantServiceProviderOptions] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [emailErrors, setEmailErrors] = useState({});

  const patch = (updates) => setFormData((prev) => ({ ...prev, ...updates }));

  const offeredServices = normalizeOfferedServices(formData.offer_services);

  const updateOfferedService = (index, value) => {
    const next = offeredServices.map((row, i) =>
      i === index
        ? {
            ...row,
            service_content: String(value).slice(0, OFFERED_SERVICE_MAX_LENGTH),
          }
        : row
    );
    patch({ offer_services: next });
  };

  const addOfferedService = () => {
    patch({ offer_services: [...offeredServices, createEmptyOfferedService()] });
  };

  const removeOfferedService = (index) => {
    if (offeredServices.length <= 1) {
      patch({ offer_services: [createEmptyOfferedService()] });
      return;
    }
    patch({ offer_services: offeredServices.filter((_, i) => i !== index) });
  };

  useEffect(() => {
    if ((!open && !isViewMode) || lookupsLoaded.current) return;
    lookupsLoaded.current = true;
    setLoadingLookups(true);
    Promise.all([
      dispatch(getEquipmentTypes()).unwrap(),
      dispatch(getManufacturers()).unwrap(),
      dispatch(getBusinessCategories()).unwrap(),
      dispatch(getLeases()).unwrap(),
      dispatch(getMerchantServiceProviders()).unwrap(),
    ])
      .then(([typesRes, manRes, catRes, leaseRes, mspRes]) => {
        const types = extractList(typesRes);
        const mans = extractList(manRes);
        const cats = extractList(catRes);
        const leases = extractList(leaseRes);
        const msps = extractList(mspRes);
        setEquipmentTypeOptions(
          Array.isArray(types) ? types.map((item) => ({ id: item?.id, name: item?.name })) : []
        );
        setManufacturerOptions(
          Array.isArray(mans) ? mans.map((item) => ({ id: item?.id, name: item?.name })) : []
        );
        setBusinessCategoryOptions(
          Array.isArray(cats) ? cats.map((item) => ({ id: item?.id, name: item?.name })) : []
        );
        setLeaseOptions(
          Array.isArray(leases) ? leases.map((item) => ({ id: item?.id, name: item?.name })) : []
        );
        setMerchantServiceProviderOptions(
          Array.isArray(msps) ? msps.map((item) => ({ id: item?.id, name: item?.name })) : []
        );
      })
      .catch(() => {})
      .finally(() => setLoadingLookups(false));
  }, [open, isViewMode, dispatch]);

  useEffect(() => {
    const tid = formData.equipment_type_id;
    const mid = formData.manufacturer_id;
    if (!tid || !mid) {
      setEquipmentModelOptions([]);
      return;
    }
    let cancelled = false;
    setLoadingLookups(true);
    dispatch(getEquipmentModels({ equipment_type_id: tid, manufacturer_id: mid }))
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        const list = extractList(res);
        setEquipmentModelOptions(
          Array.isArray(list) ? list.map((item) => ({ id: item?.id, name: item?.name })) : []
        );
      })
      .catch(() => setEquipmentModelOptions([]))
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.equipment_type_id, formData.manufacturer_id, dispatch]);

  const selectedType = equipmentTypeOptions.find((o) => o.id === formData.equipment_type_id) || null;
  const selectedMan = manufacturerOptions.find((o) => o.id === formData.manufacturer_id) || null;
  const selectedModel = equipmentModelOptions.find((o) => o.id === formData.equipment_model_id) || null;
  const selectedLease = leaseOptions.find((o) => o.id === formData.lease_id) || null;
  const selectedMsp =
    merchantServiceProviderOptions.find((o) => o.id === formData.merchant_service_provider_id) || null;

  const viewValue = (key, multiline = false) => {
    const v = formData[key];
    const text =
      v !== undefined && v !== null && v !== '' ? String(v) : 'N/A';
    return (
      <Typography variant="body1" sx={multiline ? viewModeTextAreaStyle : viewModeFieldStyle}>
        {text}
      </Typography>
    );
  };

  const editTextField = (key, label, opts = {}) => {
    const { multiline = false, type = 'text', placeholder = '', minRows = 3 } = opts;
    const isEmail = type === 'email';
    return (
      <TextField
        fullWidth
        label={label}
        placeholder={placeholder}
        type={type}
        value={formData[key] ?? ''}
        onChange={(e) => {
          patch({ [key]: e.target.value });
          if (isEmail && emailErrors[key]) {
            setEmailErrors((prev) => ({ ...prev, [key]: '' }));
          }
        }}
        onBlur={
          isEmail
            ? (e) => {
                const value = e.target.value;
                setEmailErrors((prev) => ({
                  ...prev,
                  [key]: value && !isValidEmail(value) ? 'Email is invalid' : '',
                }));
              }
            : undefined
        }
        error={isEmail && !!emailErrors[key]}
        helperText={isEmail ? emailErrors[key] : undefined}
        disabled={disabled}
        multiline={multiline}
        rows={multiline ? minRows : undefined}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        sx={textFieldSx}
      />
    );
  };

  const editNumberField = (key, label, placeholder = '') => (
    <TextField
      fullWidth
      label={label}
      placeholder={placeholder}
      type="number"
      value={formData[key] ?? ''}
      onChange={(e) => patch({ [key]: e.target.value })}
      disabled={disabled}
      inputProps={{ min: 0 }}
      sx={textFieldSx}
    />
  );

  /** Must be a direct child of Grid container — bare TextFields break the 12-column layout */
  const editNumberInGrid = (key, label, placeholder) => (
    <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
      {editNumberField(key, label, placeholder)}
    </Grid>
  );

  const viewLabeled = (label, key, multiline = false) => (
    <Grid item xs={12} md={6}>
      <Typography variant="subtitle1" sx={viewModeTitleStyle}>
        {label}
      </Typography>
      {viewValue(key, multiline)}
    </Grid>
  );

  const editLabeled = (label, key, opts = {}) => (
    <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
      {editTextField(key, label, opts)}
    </Grid>
  );

  const body = (
    <Grid container spacing={3}>
      {isViewMode ? (
        <>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Submission date
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {formData.submission_date
                ? (() => {
                    try {
                      return new Date(formData.submission_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      });
                    } catch {
                      return String(formData.submission_date);
                    }
                  })()
                : 'N/A'}
            </Typography>
          </Grid>
          {viewLabeled('Legal business name', 'legal_business_name')}
          {viewLabeled('Federal tax ID', 'federal_tax_id')}
          {viewLabeled('SSN', 'ssn')}
          {viewLabeled('EIN', 'ein')}
          {viewLabeled('Business years', 'business_years')}
          {viewLabeled('Business months', 'business_months')}
          {viewLabeled('Business category', 'business_category')}
          {viewLabeled('Monthly volume', 'monthly_volume')}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Equipment type
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {selectedType?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Manufacturer
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {selectedMan?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Equipment model
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {selectedModel?.name || 'N/A'}
            </Typography>
          </Grid>
          {viewLabeled('Personal phone', 'personal_phone')}
          {viewLabeled('Legal business phone', 'legal_business_phone')}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Legal business address
            </Typography>
            {viewValue('legal_business_address', true)}
          </Grid>
          {viewLabeled('Legal business email', 'legal_business_email')}
          {viewLabeled('Monthly lease', 'monthly_lease')}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Lease
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {selectedLease?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Merchant service provider
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {selectedMsp?.name || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Is bundled
            </Typography>
            <Typography variant="body1" sx={viewModeFieldStyle}>
              {formData.is_bundled ? 'Yes' : 'No'}
            </Typography>
          </Grid>
          {viewLabeled('Processing offered', 'processing_offered')}
          {viewLabeled('Avg month sales', 'avg_month_sales')}
          {viewLabeled('Highest ticket', 'highest_ticket')}
          {viewLabeled('Average tickets', 'average_tickets')}
          {viewLabeled('Bank name', 'bank_name')}
          {viewLabeled('Routing number', 'routing_number')}
          {viewLabeled('Account number', 'account_number')}
          {viewLabeled('FNS number for EBT', 'fns_number_for_ebt')}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              DBA address
            </Typography>
            {viewValue('dba_address', true)}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={viewModeTitleStyle}>
              Additional note
            </Typography>
            {viewValue('additional_note', true)}
          </Grid>
          {showOfferServices && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ ...viewModeTitleStyle, mb: 1.5 }}>
                Offered Services
              </Typography>
              {offeredServices.some((row) => getOfferedServiceContent(row)) ? (
                offeredServices
                  .filter((row) => getOfferedServiceContent(row))
                  .map((row, index) => (
                    <Box
                      key={row.id ?? `offered-view-${index}`}
                      sx={{
                        mb: 1.5,
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        backgroundColor: 'grey.50',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {getOfferedServiceContent(row)}
                      </Typography>
                    </Box>
                  ))
              ) : (
                <Typography variant="body1" sx={viewModeFieldStyle}>
                  —
                </Typography>
              )}
            </Grid>
          )}
          {!hideAttachments && (
            <Grid item xs={12}>
              <LeadAttachmentsUpload
                readOnly
                initialAttachments={formData.attachments}
              />
            </Grid>
          )}
        </>
      ) : (
        <>
          {editLabeled('Submission date', 'submission_date', { type: 'date' })}
          {editLabeled('Legal business name', 'legal_business_name', {
            placeholder: 'Enter legal business name',
          })}
          {editLabeled('Federal tax ID', 'federal_tax_id', { placeholder: 'Enter federal tax ID / EIN' })}
          {editLabeled('SSN', 'ssn', { placeholder: 'Enter SSN' })}
          {editLabeled('EIN', 'ein', { placeholder: 'Enter EIN' })}
          {editNumberInGrid('business_years', 'Business years', 'Years in business')}
          {editNumberInGrid('business_months', 'Business months', 'Months in business')}
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <Autocomplete
              options={businessCategoryOptions}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={businessCategoryOptions.find((o) => o.name === formData.business_category) || null}
              loading={loadingLookups}
              onChange={(_, v) => patch({ business_category: v?.name || '' })}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Business category"
                  placeholder="Select business category"
                  fullWidth
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>
          {editLabeled('Monthly volume', 'monthly_volume', { placeholder: 'e.g. $50,000' })}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={equipmentTypeOptions}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedType}
              loading={loadingLookups}
              onChange={(_, v) =>
                patch({
                  equipment_type_id: v?.id || '',
                  manufacturer_id: '',
                  equipment_model_id: '',
                })
              }
              disabled={disabled}
              renderInput={(params) => (
                <TextField {...params} label="Equipment type" placeholder="Select type" fullWidth sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <Autocomplete
              options={manufacturerOptions}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedMan}
              onChange={(_, v) =>
                patch({
                  manufacturer_id: v?.id || '',
                  equipment_model_id: '',
                })
              }
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manufacturer"
                  placeholder="Select manufacturer"
                  fullWidth
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <Autocomplete
              options={equipmentModelOptions}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedModel}
              onChange={(_, v) => patch({ equipment_model_id: v?.id || '' })}
              disabled={disabled || !formData.equipment_type_id || !formData.manufacturer_id}
              loading={loadingLookups}
              renderInput={(params) => (
                <TextField {...params} label="Equipment model" placeholder="Select model" fullWidth sx={textFieldSx} />
              )}
            />
          </Grid>
          {editLabeled('Personal phone', 'personal_phone', { placeholder: 'Personal phone number' })}
          {editLabeled('Legal business phone', 'legal_business_phone', { placeholder: 'Legal business phone' })}
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <TextField
              fullWidth
              label="Legal business address"
              placeholder="Enter legal business address"
              value={formData.legal_business_address ?? ''}
              onChange={(e) => patch({ legal_business_address: e.target.value })}
              disabled={disabled}
              multiline
              rows={3}
              sx={textFieldSx}
            />
          </Grid>
          {editLabeled('Legal business email', 'legal_business_email', {
            type: 'email',
            placeholder: 'name@example.com',
          })}
          {editLabeled('Monthly lease', 'monthly_lease', { placeholder: 'Monthly lease amount' })}
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <Autocomplete
              options={leaseOptions}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedLease}
              loading={loadingLookups}
              onChange={(_, v) => patch({ lease_id: v?.id || '' })}
              disabled={disabled}
              renderInput={(params) => (
                <TextField {...params} label="Lease" placeholder="Select lease" fullWidth sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <Autocomplete
              options={merchantServiceProviderOptions}
              getOptionLabel={(o) => o.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              value={selectedMsp}
              loading={loadingLookups}
              onChange={(_, v) => patch({ merchant_service_provider_id: v?.id || '' })}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Merchant service provider"
                  placeholder="Select merchant service provider"
                  fullWidth
                  sx={textFieldSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 0, md: 2 } }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.is_bundled}
                  onChange={(e) => patch({ is_bundled: e.target.checked })}
                  disabled={disabled}
                  color="primary"
                />
              }
              label="Is bundled"
              sx={{ m: 0 }}
            />
          </Grid>
          {editLabeled('Processing offered', 'processing_offered', { placeholder: 'Processing offered' })}
          {editNumberInGrid('avg_month_sales', 'Avg month sales', 'Average monthly sales')}
          {editLabeled('Highest ticket', 'highest_ticket', { placeholder: 'Highest ticket amount' })}
          {editLabeled('Average tickets', 'average_tickets', {
            placeholder: 'e.g. $250 or typical ticket size',
          })}
          {editLabeled('Bank name', 'bank_name', { placeholder: 'Bank name' })}
          {editLabeled('Routing number', 'routing_number', { placeholder: '9-digit routing number' })}
          {editLabeled('Account number', 'account_number', { placeholder: 'Account number' })}
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <TextField
              fullWidth
              label="FNS number for EBT"
              placeholder="FNS number for EBT"
              value={formData.fns_number_for_ebt ?? ''}
              onChange={(e) => patch({ fns_number_for_ebt: e.target.value })}
              disabled={disabled}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ alignSelf: 'flex-start' }}>
            <TextField
              fullWidth
              label="DBA address"
              placeholder="Enter DBA address"
              value={formData.dba_address ?? ''}
              onChange={(e) => patch({ dba_address: e.target.value })}
              disabled={disabled}
              multiline
              rows={3}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} sx={{ alignSelf: 'flex-start' }}>
            <TextField
              fullWidth
              label="Additional note"
              placeholder="Additional notes"
              value={formData.additional_note ?? ''}
              onChange={(e) => patch({ additional_note: e.target.value })}
              disabled={disabled}
              multiline
              rows={3}
              sx={textFieldSx}
            />
          </Grid>
          {showOfferServices && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Offered Services
                </Typography>
                <IconButton
                  color="primary"
                  onClick={addOfferedService}
                  disabled={disabled}
                  aria-label="Add offered service"
                  size="small"
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              {offeredServices.map((row, index) => (
                <Box
                  key={`offered-edit-${index}`}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Service offered"
                    placeholder="Enter service offered"
                    value={getOfferedServiceContent(row)}
                    onChange={(e) => updateOfferedService(index, e.target.value)}
                    disabled={disabled}
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: OFFERED_SERVICE_MAX_LENGTH }}
                    helperText={`${getOfferedServiceContent(row).length}/${OFFERED_SERVICE_MAX_LENGTH}`}
                    FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                    sx={textFieldSx}
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeOfferedService(index)}
                    disabled={disabled || offeredServices.length <= 1}
                    aria-label="Remove offered service"
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          )}
          {!hideAttachments && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                Attachments
              </Typography>
              <LeadAttachmentsUpload
                leadId={leadId}
                disabled={disabled}
                attachmentIds={formData.attachment_ids || []}
                onAttachmentIdsChange={(ids) => patch({ attachment_ids: ids })}
                hydrateToken={attachmentsHydrateToken}
                initialAttachments={formData.attachments}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );

  if (isViewMode) {
    const hasAny =
      [
        'submission_date',
        'legal_business_name',
        'federal_tax_id',
        'ssn',
        'ein',
        'equipment_type_id',
        'manufacturer_id',
        'equipment_model_id',
        'personal_phone',
        'legal_business_phone',
        'legal_business_address',
        'legal_business_email',
        'monthly_lease',
        'processing_offered',
        'monthly_volume',
        'avg_month_sales',
        'highest_ticket',
        'average_tickets',
        'bank_name',
        'routing_number',
        'account_number',
        'fns_number_for_ebt',
        'dba_address',
        'additional_note',
        'lease_id',
        'merchant_service_provider_id',
      ].some((k) => formData[k] !== undefined && formData[k] !== '' && formData[k] !== null) ||
      formData.is_bundled ||
      (showOfferServices &&
        Array.isArray(formData.offer_services) &&
        formData.offer_services.some((row) => getOfferedServiceContent(row))) ||
      (Array.isArray(formData.attachments) && formData.attachments.length > 0) ||
      (Array.isArray(formData.attachment_ids) && formData.attachment_ids.length > 0);

    if (!hasAny) return null;

    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="h6"
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            mb: 2,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Additional details
        </Typography>
        {body}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        type="button"
        variant="outlined"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        sx={{
          mb: 2,
          px: 2,
          py: 1,
          borderColor: 'blue.600',
          color: 'primary.main',
          fontWeight: 600,
          '&:hover': {
            borderColor: 'blue.700',
            bgcolor: 'rgba(21, 74, 138, 0.04)',
          },
        }}
      >
        {open ? 'Hide additional details' : 'Additional details'}
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            pt: 3,
            pb: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {body}
        </Box>
      </Collapse>
    </Box>
  );
}
